import { watch } from 'vue'
import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import { debounce } from '@/utils/helpers'
import { MAX_READ_TIME, TAG_MATCH } from '@/utils/constants'
import errorHandler from '@/plugins/error-handler'
import { nextTopSortOrder } from '@/utils/sort-order'

/**
 * Builds a VuexORM `tagIds` where-predicate honoring the tagMatch operator:
 * 'all' (AND) requires every selected tag to be present, otherwise 'any' (OR)
 * requires at least one. Mirrors the backend $all / $in behavior.
 */
export const tagFilterPredicate = filters => tagIds =>
  filters.tagMatch === TAG_MATCH.ALL
    ? filters.tags.every(t => tagIds.includes(t))
    : filters.tags.some(t => tagIds.includes(t))

/**
 * Key-order-independent deep equality for filter objects.
 * Sorts keys before stringifying so { a:1, b:2 } equals { b:2, a:1 }.
 */
export const deepEqualSorted = (a, b) => {
  const sorted = obj => JSON.stringify(obj, Object.keys(obj || {}).sort())
  return sorted(a) === sorted(b)
}

/**
 * Computes which filters have changed from defaults.
 * Returns false if no filters active, or an array of active filter names.
 *
 * @param {Object} filters - Current filter values
 * @param {Object} defaults - Default filter values
 * @param {Object} [options]
 * @param {boolean} [options.checkFavorite=false] - Include isFavorite in check
 * @param {boolean} [options.checkResourceType=false] - Include resourceType in check
 */
export const computeFiltersActive = (filters, defaults, { checkFavorite = false, checkResourceType = false } = {}) => {
  const changes = []
  if (filters.tags?.length) changes.push('tags')
  if (checkFavorite && filters.isFavorite) changes.push('favorites')
  if (checkResourceType && filters.resourceType) changes.push('resourceType')
  if (filters.languages?.length) changes.push('languages')
  if (filters.addedBy?.length) changes.push('addedBy')
  if (JSON.stringify(filters.readTime) !== JSON.stringify(defaults.readTime)) changes.push('readTime')
  if (filters.search?.trim()) changes.push('search')
  return changes.length ? changes : false
}

/**
 * Removes cached collection items for a single collection from VuexORM.
 *
 * @param {string|null|undefined} collectionId
 * @returns {Promise<void>|void}
 */
export const clearCollectionItemsCache = collectionId =>
  CollectionItem.delete(item => item.ownerId === collectionId)

/**
 * Queries pinned items from VuexORM.
 *
 * @param {string} collectionId
 * @param {Object} [filters] - Current filter values
 * @param {Object} [options]
 * @param {boolean} [options.applyItemFilters=false] - Apply isFavorite/resourceType/tags filters
 */
export const queryPinnedItems = (collectionId, filters, { applyItemFilters = false } = {}) => {
  const query = CollectionItem.query()
    .where('ownerId', collectionId)
    .where('pinPosition', v => v != null)
    .with(['tags'])

  if (applyItemFilters && filters) {
    if (filters.isFavorite) query.where('isFavorite', true)
    if (filters.resourceType) query.where('resourceType', filters.resourceType)
    if (filters.tags?.length) {
      query.where('tagIds', tagFilterPredicate(filters))
    }
  }

  return query.orderBy('pinPosition', 'asc').get()
}

/**
 * Returns a sortOrder that places an item at the top of a collection's regular
 * (unpinned) list. Used when inserting a newly-created item so it appears first.
 *
 * @param {string} collectionId
 */
export const getTopSortOrder = collectionId =>
  nextTopSortOrder(
    CollectionItem.query()
      .where('ownerId', collectionId)
      .where('pinPosition', v => v == null)
      .get()
      .map(i => i.sortOrder),
  )

/**
 * Queries regular (unpinned) items from VuexORM.
 *
 * @param {string} collectionId
 * @param {Object} filters - Current filter values
 * @param {number} show - Limit for results
 * @param {Object} [options]
 * @param {boolean} [options.applyItemFilters=false] - Apply isFavorite/resourceType filters
 */
export const queryRegularItems = (collectionId, filters, show, { applyItemFilters = false, skipPinFilter = false } = {}) => {
  const query = CollectionItem.query()
    .where('ownerId', collectionId)
  if (!skipPinFilter) {
    query.where('pinPosition', v => v == null)
  }
  query.with(['tags']).limit(show)

  if (applyItemFilters && filters) {
    if (filters.isFavorite) query.where('isFavorite', true)
    if (filters.resourceType) query.where('resourceType', filters.resourceType)
  }

  if (filters?.tags?.length) {
    query.where('tagIds', tagFilterPredicate(filters))
  }

  if (filters?.search) {
    query.orderBy('searchScore', 'desc')
  } else {
    query.orderBy('sortOrder', 'asc')
  }

  return query.get()
}

/**
 * Creates a guarded async loader that prevents concurrent calls and manages a loading flag.
 * Used for stats, tags, and similar one-shot loading patterns.
 *
 * @param {import('vue').Ref} loading - Reactive loading state object
 * @param {string} key - Key in the loading object (e.g. 'stats', 'tags')
 * @param {Function} asyncAction - Async function to execute
 * @returns {Function} Guarded loader: (forced?: boolean) => Promise
 */
export const createGuardedLoader = (loading, key, asyncAction) => async (forced = false) => {
  if (loading.value[key] && !forced) return
  loading.value[key] = true
  try {
    return await asyncAction()
  } catch (error) {
    errorHandler.toast(error)
  } finally {
    loading.value[key] = false
  }
}

/**
 * Normalizes readTime filter values for API params.
 * Returns null if no readTime constraint is active.
 */
export const buildReadTimeParams = (readTime) => {
  if (!readTime) return null
  const normalized = [
    readTime[0] || false,
    readTime[1] >= MAX_READ_TIME ? false : readTime[1],
  ]
  return normalized.some(Boolean) ? normalized : null
}

/**
 * Creates an infinite-scroll handler that triggers loading when near the bottom.
 *
 * @param {Object} options
 * @param {Function} options.isLoading - Returns true when a load is already in progress
 * @param {Function} options.hasMore - Returns true when more data is available
 * @param {Function} options.loadMore - Called to fetch the next page
 * @returns {Function} Scroll event handler
 */
export const createScrollHandler = ({ isLoading, hasMore, loadMore }) => () => {
  const bottomOfWindow =
    window.innerHeight + window.scrollY >= document.documentElement.offsetHeight - 1000
  if (bottomOfWindow && !isLoading() && hasMore()) {
    loadMore()
  }
}

/**
 * Sets up a deep watcher on filters that debounces search changes
 * and immediately reacts to other filter changes.
 *
 * @param {Object} options
 * @param {import('vue').ComputedRef} options.filters - Reactive filter object to watch
 * @param {import('vue').Ref} [options.isInitializing] - When truthy, watcher is skipped
 * @param {Function} options.onDebouncedSearch - Called (debounced) when search changes
 * @param {Function} options.onImmediateChange - Called immediately for non-search filter changes
 * @param {number} [options.minSearchLength=2] - Minimum search length to trigger (0 = any change)
 */
export const createFilterWatcher = ({
  filters,
  isInitializing,
  onDebouncedSearch,
  onImmediateChange,
  minSearchLength = 2,
}) => {
  const debouncedFn = debounce(onDebouncedSearch, 500)

  watch(filters, (newFilters, oldFilters) => {
    if (deepEqualSorted(newFilters, oldFilters)) return
    if (isInitializing?.value) return

    if (oldFilters && (newFilters?.search || '') !== (oldFilters?.search || '')) {
      const len = (newFilters?.search || '').length
      if (len >= minSearchLength || len === 0) debouncedFn()
    } else {
      onImmediateChange()
    }
  }, { deep: true })
}

/**
 * Creates a guarded items loader that handles the common pattern of:
 * guard -> set loading -> dispatch -> update next/filteredCount/show -> catch -> finally.
 *
 * @param {Object} options
 * @param {import('vue').Ref} options.loading - Reactive loading state object
 * @param {Function} options.getAction - Async function that returns { results, next, itemCount }
 * @param {Function} options.setNext - Mutation to set the next cursor
 * @param {Function} options.setFilteredItemCount - Mutation to set filtered item count
 * @param {Function} options.setShow - Mutation to set the show count
 * @param {Function} options.getShow - Getter for the current show count
 * @param {import('vue').Ref} options.pinnedFilteredCount - Ref tracking pinned item count
 * @param {boolean} [options.rethrow=false] - Whether to re-throw errors after toasting
 * @returns {Function} Loader: (forced?: boolean) => Promise
 */
export const createItemsLoader = ({
  loading, getAction, setNext, setFilteredItemCount,
  setShow, getShow, pinnedFilteredCount, rethrow = false,
}) => async (forced = false) => {
  if (loading.value.items && !forced) return
  loading.value.items = true
  try {
    const { results, next, itemCount } = await getAction()
    setNext(next)
    setFilteredItemCount((itemCount || 0) + pinnedFilteredCount.value)
    setShow(getShow() + results.length)
  } catch (error) {
    errorHandler.toast(error)
    if (rethrow) throw error
  } finally {
    loading.value.items = false
  }
}
